/**
 * CoConnect — Enterprise UI Primitives
 * Single import surface for shared presentational components.
 * No business logic. No cartoony icons. No colorful gradients on cards.
 */
import React from 'react';

/* ─── BrandMark ───────────────────────────────────────────────── */
type PortalAccent = 'admin' | 'agrarian' | 'cargo' | 'terrain' | 'analytics' | 'cocoa';
const accentSurface: Record<PortalAccent, string> = {
  admin:     'brand-surface-admin',
  agrarian:  'brand-surface-agrarian',
  cargo:     'brand-surface-cargo',
  terrain:   'brand-surface-terrain',
  analytics: 'brand-surface-analytics',
  cocoa:     'brand-surface-cocoa',
};
const accentSolid: Record<PortalAccent, string> = {
  admin:     'bg-admin-600',
  agrarian:  'bg-agrarian-700',
  cargo:     'bg-cargo-600',
  terrain:   'bg-terrain-700',
  analytics: 'bg-analytics-700',
  cocoa:     'bg-cocoa-700',
};

export const BrandMark: React.FC<{
  size?: 'sm' | 'md' | 'lg' | 'xl';
  accent?: PortalAccent;
  /** Optional override — if omitted, renders cocoa pod glyph */
  glyph?: React.ReactNode;
  className?: string;
}> = ({ size = 'md', accent = 'cocoa', glyph, className = '' }) => {
  const dims = {
    sm: 'w-9 h-9 rounded-lg',
    md: 'w-11 h-11 rounded-xl',
    lg: 'w-14 h-14 rounded-2xl',
    xl: 'w-20 h-20 rounded-2xl',
  }[size];
  const glyphSize = {
    sm: 'w-5 h-5',
    md: 'w-6 h-6',
    lg: 'w-8 h-8',
    xl: 'w-10 h-10',
  }[size];
  return (
    <div className={`${accentSurface[accent]} ${dims} flex items-center justify-center shadow-e2 ${className}`}>
      {glyph ?? <CocoaPod className={`${glyphSize} text-white`} />}
    </div>
  );
};

/** Custom SVG cocoa pod mark — geometric, enterprise, not cartoony */
export const CocoaPod: React.FC<{ className?: string }> = ({ className = '' }) => (
  <svg viewBox="0 0 24 24" fill="none" className={className} aria-hidden>
    <path
      d="M12 2.5c-2.4 0-4.3 1.4-5.4 3.3C5.5 7.7 5 9.9 5 12c0 4.7 2.7 9.5 7 9.5s7-4.8 7-9.5c0-2.1-.5-4.3-1.6-6.2C16.3 3.9 14.4 2.5 12 2.5z"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinejoin="round"
      fill="currentColor"
      fillOpacity="0.18"
    />
    <path
      d="M12 5.5v15M9 6.5l-1.5 14M15 6.5l1.5 14"
      stroke="currentColor"
      strokeWidth="1.2"
      strokeLinecap="round"
      opacity="0.6"
    />
  </svg>
);

/* ─── Card ────────────────────────────────────────────────────── */
export const Card: React.FC<{
  children: React.ReactNode;
  className?: string;
  as?: React.ElementType;
  elevated?: boolean;
  inset?: boolean;
  padding?: 'none' | 'sm' | 'md' | 'lg';
  onClick?: (e: React.MouseEvent) => void;
}> = ({ children, className = '', as: As = 'div', elevated = false, inset = false, padding = 'md', onClick }) => {
  const pad = {
    none: '',
    sm: 'p-3',
    md: 'p-5',
    lg: 'p-6 md:p-7',
  }[padding];
  const surface = inset
    ? 'surface-inset'
    : elevated
      ? 'surface-elevated'
      : 'surface-card';
  return (
    <As
      onClick={onClick}
      className={`${surface} ${pad} ${onClick ? 'cursor-pointer lift' : ''} ${className}`}
    >
      {children}
    </As>
  );
};

/* ─── SectionHeader ───────────────────────────────────────────── */
export const SectionHeader: React.FC<{
  eyebrow?: string;
  title: string;
  subtitle?: string;
  actions?: React.ReactNode;
  icon?: React.ReactNode;
  className?: string;
}> = ({ eyebrow, title, subtitle, actions, icon, className = '' }) => (
  <div className={`ui-section-header flex items-start justify-between gap-4 ${className}`}>
    <div className="ui-section-header-main flex items-start gap-3 min-w-0">
      {icon && (
        <div className="ui-section-header-icon shrink-0 w-9 h-9 rounded-lg bg-ink-100 dark:bg-ink-800 flex items-center justify-center text-ink-600 dark:text-ink-300">
          {icon}
        </div>
      )}
      <div className="ui-section-header-copy min-w-0">
        {eyebrow && <p className="section-eyebrow mb-1">{eyebrow}</p>}
        <h2 className="section-title truncate">{title}</h2>
        {subtitle && <p className="text-sm text-ink-500 dark:text-ink-400 mt-1 text-pretty">{subtitle}</p>}
      </div>
    </div>
    {actions && <div className="ui-section-header-actions shrink-0 flex items-center gap-2">{actions}</div>}
  </div>
);

/* ─── MetricTile ──────────────────────────────────────────────── */
export const MetricTile: React.FC<{
  label: string;
  value: React.ReactNode;
  hint?: React.ReactNode;
  icon?: React.ReactNode;
  trend?: { value: string; direction: 'up' | 'down' | 'flat'; tone?: 'positive' | 'negative' | 'neutral' };
  accent?: PortalAccent;
  className?: string;
  onClick?: () => void;
}> = ({ label, value, hint, icon, trend, accent, className = '', onClick }) => {
  const accentText = accent ? `text-${accent}-600 dark:text-${accent}-400` : 'text-ink-900 dark:text-ink-50';
  const trendTone = trend?.tone ?? (trend?.direction === 'up' ? 'positive' : trend?.direction === 'down' ? 'negative' : 'neutral');
  const trendColor = {
    positive: 'text-success-600 dark:text-success-500',
    negative: 'text-danger-600  dark:text-danger-500',
    neutral:  'text-ink-500 dark:text-ink-400',
  }[trendTone];
  const TrendArrow = trend?.direction === 'up' ? '↑' : trend?.direction === 'down' ? '↓' : '→';
  return (
    <Card onClick={onClick} padding="md" className={`ui-metric-tile ${className}`}>
      <div className="ui-metric-body flex items-start justify-between">
        <div className="ui-metric-copy min-w-0 flex-1">
          <p className="ui-metric-label text-2xs font-medium uppercase tracking-micro text-ink-500 dark:text-ink-400 truncate">{label}</p>
          <p className={`ui-metric-value mt-2 text-2xl font-semibold nums tracking-tight ${accentText}`}>{value}</p>
          {hint && <p className="ui-metric-hint mt-1 text-xs text-ink-500 dark:text-ink-400 truncate">{hint}</p>}
          {trend && (
            <p className={`mt-2 inline-flex items-center gap-1 text-xs font-medium nums ${trendColor}`}>
              <span aria-hidden>{TrendArrow}</span>
              <span>{trend.value}</span>
            </p>
          )}
        </div>
        {icon && (
          <div className="ui-metric-icon shrink-0 ml-3 w-9 h-9 rounded-lg bg-ink-100 dark:bg-ink-800 flex items-center justify-center text-ink-500 dark:text-ink-400">
            {icon}
          </div>
        )}
      </div>
    </Card>
  );
};

/* ─── StatusPill ──────────────────────────────────────────────── */
const pillToneMap = {
  success: 'pill-success',
  warning: 'pill-warning',
  danger:  'pill-danger',
  info:    'pill-info',
  neutral: 'pill-neutral',
} as const;
export type PillTone = keyof typeof pillToneMap;

export const StatusPill: React.FC<{
  tone?: PillTone;
  children: React.ReactNode;
  dot?: boolean;
  className?: string;
}> = ({ tone = 'neutral', children, dot = false, className = '' }) => {
  const dotColor = {
    success: 'bg-success-500',
    warning: 'bg-warning-500',
    danger:  'bg-danger-500',
    info:    'bg-info-500',
    neutral: 'bg-ink-400',
  }[tone];
  return (
    <span className={`${pillToneMap[tone]} ${className}`}>
      {dot && <span className={`w-1.5 h-1.5 rounded-full ${dotColor}`} />}
      {children}
    </span>
  );
};

/* ─── Smart status badge — auto-maps known statuses to tones ─── */
const statusToneMap: Record<string, PillTone> = {
  // payment
  PAID: 'success', PENDING: 'warning', COMPLETED: 'success', DELIVERED: 'success',
  PROCESSING: 'info', REJECTED: 'danger', CANCELLED: 'neutral', FAILED: 'danger',
  APPROVED: 'success', AUTO_APPROVED: 'success',
  // truck
  AVAILABLE: 'success', IN_TRANSIT: 'info', MAINTENANCE: 'warning', IDLE: 'neutral',
  // quality
  A: 'success', B: 'warning', C: 'danger',
  // eudr
  COMPLIANT: 'success', NON_COMPLIANT: 'danger', UNDER_REVIEW: 'warning', PENDING_REVIEW: 'warning',
};
export const SmartStatus: React.FC<{ status?: string; className?: string }> = ({ status, className = '' }) => {
  if (!status) return <StatusPill tone="neutral" className={className}>—</StatusPill>;
  const tone = statusToneMap[status] || 'neutral';
  const display = status.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, c => c.toUpperCase());
  return <StatusPill tone={tone} dot className={className}>{display}</StatusPill>;
};

/* ─── Spinner ─────────────────────────────────────────────────── */
export const Spinner: React.FC<{ className?: string; size?: 'sm' | 'md' | 'lg' }> = ({ className = '', size = 'md' }) => {
  const s = { sm: 'w-4 h-4', md: 'w-5 h-5', lg: 'w-8 h-8' }[size];
  return (
    <svg className={`animate-spin ${s} ${className}`} viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle className="opacity-20" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
      <path className="opacity-90" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
    </svg>
  );
};

/* ─── EmptyState ──────────────────────────────────────────────── */
export const EmptyState: React.FC<{
  icon?: React.ReactNode;
  title: string;
  description?: string;
  action?: React.ReactNode;
  className?: string;
}> = ({ icon, title, description, action, className = '' }) => (
  <div className={`empty-state ${className}`}>
    {icon && (
      <div className="w-12 h-12 rounded-xl bg-ink-100 dark:bg-ink-800 flex items-center justify-center text-ink-400 dark:text-ink-500 mb-3">
        {icon}
      </div>
    )}
    <h3 className="text-sm font-semibold text-ink-800 dark:text-ink-100">{title}</h3>
    {description && <p className="mt-1 text-sm text-ink-500 dark:text-ink-400 max-w-xs text-pretty">{description}</p>}
    {action && <div className="mt-4">{action}</div>}
  </div>
);

/* ─── ProgressBar ─────────────────────────────────────────────── */
export const ProgressBar: React.FC<{
  value: number; // 0-100
  tone?: 'default' | 'success' | 'warning' | 'danger';
  className?: string;
  showLabel?: boolean;
  label?: string;
}> = ({ value, tone = 'default', className = '', showLabel = false, label }) => {
  const v = Math.max(0, Math.min(100, value));
  const toneClass = {
    default: 'bg-ink-900 dark:bg-ink-100',
    success: 'bg-success-500',
    warning: 'bg-warning-500',
    danger:  'bg-danger-500',
  }[tone];
  return (
    <div className={className}>
      {(showLabel || label) && (
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-xs text-ink-500 dark:text-ink-400">{label}</span>
          {showLabel && <span className="text-xs font-medium nums text-ink-700 dark:text-ink-200">{v.toFixed(0)}%</span>}
        </div>
      )}
      <div className="h-1.5 bg-ink-200 dark:bg-ink-800 rounded-full overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-500 ease-app ${toneClass}`}
          style={{ width: `${v}%` }}
        />
      </div>
    </div>
  );
};

/* ─── Skeleton ────────────────────────────────────────────────── */
export const Skeleton: React.FC<{ className?: string }> = ({ className = '' }) => (
  <div className={`skeleton ${className}`} aria-hidden />
);

/* ─── IconButton ──────────────────────────────────────────────── */
export const IconButton: React.FC<{
  children: React.ReactNode;
  onClick?: (e: React.MouseEvent) => void;
  label: string;
  size?: 'sm' | 'md';
  tone?: 'ghost' | 'solid';
  className?: string;
  disabled?: boolean;
}> = ({ children, onClick, label, size = 'md', tone = 'ghost', className = '', disabled }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    aria-label={label}
    title={label}
    className={`${tone === 'solid' ? 'btn-secondary' : size === 'sm' ? 'btn-icon-sm' : 'btn-icon'} ${className}`}
  >
    {children}
  </button>
);

/* ─── Divider ─────────────────────────────────────────────────── */
export const Divider: React.FC<{ className?: string; label?: string }> = ({ className = '', label }) => (
  label ? (
    <div className={`flex items-center gap-3 ${className}`}>
      <div className="flex-1 divider" />
      <span className="text-2xs font-medium uppercase tracking-micro text-ink-400 dark:text-ink-500">{label}</span>
      <div className="flex-1 divider" />
    </div>
  ) : (
    <div className={`divider ${className}`} />
  )
);

/* ─── AppShell + TopBar + BottomNav ───────────────────────────── */

export const TopBar: React.FC<{
  brand: React.ReactNode;
  title?: string;
  subtitle?: string;
  actions?: React.ReactNode;
  className?: string;
}> = ({ brand, title, subtitle, actions, className = '' }) => (
  <header className={`topbar ${className}`}>
    <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between gap-3">
      <div className="flex items-center gap-3 min-w-0">
        {brand}
        {(title || subtitle) && (
          <div className="min-w-0 hide-mobile">
            {title && <p className="text-sm font-semibold text-ink-900 dark:text-ink-50 truncate">{title}</p>}
            {subtitle && <p className="text-xs text-ink-500 dark:text-ink-400 truncate">{subtitle}</p>}
          </div>
        )}
      </div>
      <div className="flex items-center gap-1.5">{actions}</div>
    </div>
  </header>
);

export const BottomNav: React.FC<{
  items: Array<{ id: string; label: string; icon: React.ReactNode; badge?: number; onClick: () => void; active?: boolean }>;
}> = ({ items }) => (
  <nav className="bottomnav md:hidden">
    <div className="grid" style={{ gridTemplateColumns: `repeat(${items.length}, minmax(0, 1fr))` }}>
      {items.map(item => (
        <button
          key={item.id}
          onClick={item.onClick}
          className={`relative flex flex-col items-center justify-center gap-1 py-2.5 text-2xs font-medium transition-colors ${
            item.active
              ? 'text-ink-900 dark:text-ink-50'
              : 'text-ink-500 dark:text-ink-400'
          }`}
        >
          <span className="relative">
            {item.icon}
            {item.badge ? (
              <span className="absolute -top-1.5 -right-2 min-w-4 h-4 px-1 rounded-full bg-danger-500 text-white text-2xs font-semibold flex items-center justify-center nums">
                {item.badge > 99 ? '99+' : item.badge}
              </span>
            ) : null}
          </span>
          <span className="truncate max-w-full px-1">{item.label}</span>
          {item.active && <span className="absolute top-0 left-1/2 -translate-x-1/2 w-8 h-0.5 bg-ink-900 dark:bg-ink-50 rounded-full" />}
        </button>
      ))}
    </div>
  </nav>
);

/* ─── Segmented tabs (horizontal pill-style tabs) ─────────────── */
// v2.1.1 (TABS-OVERFLOW fix): the original implementation used
// `overflow-x-auto no-scrollbar` which hid tabs that didn't fit in the
// viewport. The scrollbar was hidden via CSS, leaving no visual cue that
// more tabs existed — users couldn't discover tabs like "Harvest", "Quality",
// "Yield", "Activities", "Grade History", "Input Credit" on the Farmer portal
// (15 tabs), and 10+ tabs were hidden on the Admin portal (43 tabs).
//
// Fix: detect overflow with a ref + resize observer, and render left/right
// chevron buttons that scroll the strip by one viewport-width. Also fade the
// right edge so users see there is more to scroll. 100% additive — the
// original tab rendering logic is preserved; we only wrap it in a container
// with scroll affordances.
export const SegmentedTabs: React.FC<{
  items: Array<{ id: string; label: string; icon?: React.ReactNode; badge?: number }>;
  active: string;
  onChange: (id: string) => void;
  className?: string;
  size?: 'sm' | 'md';
}> = ({ items, active, onChange, className = '', size = 'md' }) => {
  const pad = size === 'sm' ? 'px-3 py-1.5 text-xs' : 'px-3.5 py-2 text-sm';
  // v2.1.1 (TABS-OVERFLOW fix): scroll state + ref
  const scrollRef = React.useRef<HTMLDivElement | null>(null);
  const [canScrollLeft, setCanScrollLeft] = React.useState(false);
  const [canScrollRight, setCanScrollRight] = React.useState(false);

  // Re-check overflow on mount, on resize, and after a tick (icons may load late)
  const checkOverflow = React.useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  }, []);

  React.useEffect(() => {
    checkOverflow();
    const el = scrollRef.current;
    if (!el) return;
    // Scroll the active tab into view on mount and when `active` changes
    const activeBtn = el.querySelector('[data-active="true"]') as HTMLElement | null;
    if (activeBtn) {
      activeBtn.scrollIntoView({ behavior: 'auto', block: 'nearest', inline: 'nearest' });
    }
    checkOverflow();
  }, [active, checkOverflow, items]);

  React.useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const ro = new ResizeObserver(checkOverflow);
    ro.observe(el);
    el.addEventListener('scroll', checkOverflow, { passive: true });
    return () => {
      ro.disconnect();
      el.removeEventListener('scroll', checkOverflow);
    };
  }, [checkOverflow]);

  const scrollBy = (dir: 1 | -1) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: dir * Math.max(el.clientWidth * 0.8, 200), behavior: 'smooth' });
  };

  return (
    <div className={`relative flex items-center gap-1 ${className}`}>
      {/* v2.1.1 (TABS-OVERFLOW): left chevron */}
      {canScrollLeft && (
        <button
          type="button"
          aria-label="Scroll tabs left"
          onClick={() => scrollBy(-1)}
          className="shrink-0 z-10 w-7 h-7 -ml-1 flex items-center justify-center rounded-full bg-ink-0 dark:bg-ink-800 border border-ink-200 dark:border-ink-700 shadow-e1 hover:bg-ink-50 dark:hover:bg-ink-700 transition-colors"
        >
          <svg viewBox="0 0 24 24" className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M15 18l-6-6 6-6" />
          </svg>
        </button>
      )}
      <div
        ref={scrollRef}
        className="flex gap-1 p-1 bg-ink-100 dark:bg-ink-900 rounded-xl overflow-x-auto no-scrollbar scroll-smooth"
        role="tablist"
        style={{ scrollBehavior: 'smooth' }}
      >
        {items.map(item => {
          const isActive = item.id === active;
          return (
            <button
              key={item.id}
              onClick={() => onChange(item.id)}
              role="tab"
              data-tab-id={item.id}
              aria-selected={isActive}
              data-active={isActive ? 'true' : 'false'}
              className={`relative inline-flex items-center gap-2 ${pad} font-medium rounded-lg whitespace-nowrap transition-all duration-150 ease-app ${
                isActive
                  ? 'bg-ink-0 dark:bg-ink-800 text-ink-900 dark:text-ink-50 shadow-e1'
                  : 'text-ink-500 dark:text-ink-400 hover:text-ink-800 dark:hover:text-ink-200'
              }`}
            >
              {item.icon}
              <span>{item.label}</span>
              {item.badge ? (
                <span className={`min-w-4 h-4 px-1 rounded-full text-2xs font-semibold flex items-center justify-center nums ${
                  isActive ? 'bg-ink-900 text-ink-50 dark:bg-ink-50 dark:text-ink-900' : 'bg-ink-200 dark:bg-ink-700 text-ink-600 dark:text-ink-300'
                }`}>
                  {item.badge > 99 ? '99+' : item.badge}
                </span>
              ) : null}
            </button>
          );
        })}
      </div>
      {/* v2.1.1 (TABS-OVERFLOW): right chevron — also serves as a visual cue
          that more tabs exist beyond the visible viewport. */}
      {canScrollRight && (
        <button
          type="button"
          aria-label="Scroll tabs right"
          onClick={() => scrollBy(1)}
          className="shrink-0 z-10 w-7 h-7 -mr-1 flex items-center justify-center rounded-full bg-ink-0 dark:bg-ink-800 border border-ink-200 dark:border-ink-700 shadow-e1 hover:bg-ink-50 dark:hover:bg-ink-700 transition-colors"
        >
          <svg viewBox="0 0 24 24" className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M9 18l6-6-6-6" />
          </svg>
        </button>
      )}
    </div>
  );
};

/* ─── Drawer / Sheet (bottom sheet modal for mobile) ──────────── */
export const Sheet: React.FC<{
  open: boolean;
  onClose: () => void;
  title?: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
}> = ({ open, onClose, title, children, footer }) => {
  if (!open) return null;
  return (
    <>
      <div className="sheet-backdrop animate-fade-in" onClick={onClose} />
      <div className="sheet animate-sheet-up">
        <div className="sheet-handle" />
        {title && (
          <div className="px-5 pt-2 pb-3 flex items-center justify-between">
            <h3 className="text-base font-semibold text-ink-900 dark:text-ink-50">{title}</h3>
            <IconButton label="Close" onClick={onClose} size="sm">
              <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                <path d="M6 6l12 12M6 18L18 6" />
              </svg>
            </IconButton>
          </div>
        )}
        <div className="px-5 pb-4">{children}</div>
        {footer && <div className="px-5 py-4 border-t border-ink-200 dark:border-ink-800 pb-safe">{footer}</div>}
      </div>
    </>
  );
};

/* ─── Avatar (initials) ───────────────────────────────────────── */
export const Avatar: React.FC<{
  name: string;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
}> = ({ name, size = 'md', className = '' }) => {
  const dims = {
    xs: 'w-6 h-6 text-2xs',
    sm: 'w-8 h-8 text-xs',
    md: 'w-10 h-10 text-sm',
    lg: 'w-14 h-14 text-base',
    xl: 'w-20 h-20 text-xl',
  }[size];
  const initials = name
    .split(' ')
    .map(n => n[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();
  return (
    <div className={`${dims} ${className} rounded-full bg-ink-200 dark:bg-ink-700 text-ink-700 dark:text-ink-200 font-semibold flex items-center justify-center nums`}>
      {initials}
    </div>
  );
};

/* ─── Toast / Inline Alert ────────────────────────────────────── */
export const Alert: React.FC<{
  tone?: 'info' | 'success' | 'warning' | 'danger';
  title?: string;
  children: React.ReactNode;
  className?: string;
  onClose?: () => void;
}> = ({ tone = 'info', title, children, className = '', onClose }) => {
  const toneMap = {
    info:    { bg: 'bg-info-50 dark:bg-info-500/10',     border: 'border-info-500/20',   text: 'text-info-700 dark:text-info-400',     icon: 'ℹ' },
    success: { bg: 'bg-success-50 dark:bg-success-500/10', border: 'border-success-500/20', text: 'text-success-700 dark:text-success-500', icon: '✓' },
    warning: { bg: 'bg-warning-50 dark:bg-warning-500/10', border: 'border-warning-500/20', text: 'text-warning-700 dark:text-warning-500', icon: '!' },
    danger:  { bg: 'bg-danger-50 dark:bg-danger-500/10',   border: 'border-danger-500/20',   text: 'text-danger-700 dark:text-danger-500',   icon: '×' },
  }[tone];
  return (
    <div className={`flex items-start gap-3 p-3.5 rounded-xl border ${toneMap.bg} ${toneMap.border} ${className}`}>
      <span className={`shrink-0 w-5 h-5 rounded-full bg-current ${toneMap.text} flex items-center justify-center text-2xs font-bold`}>
        {toneMap.icon}
      </span>
      <div className={`flex-1 min-w-0 text-sm ${toneMap.text}`}>
        {title && <p className="font-semibold mb-0.5">{title}</p>}
        <div className="text-pretty">{children}</div>
      </div>
      {onClose && (
        <button onClick={onClose} className={`shrink-0 ${toneMap.text} hover:opacity-70`} aria-label="Dismiss">
          <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <path d="M6 6l12 12M6 18L18 6" />
          </svg>
        </button>
      )}
    </div>
  );
};

/* ─── Pagination ──────────────────────────────────────────────── */
export const Pagination: React.FC<{
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
  className?: string;
  showPageSize?: boolean;
  onPageSizeChange?: (size: number) => void;
  pageSizeOptions?: number[];
}> = ({
  page, pageSize, total, onPageChange, className = '',
  showPageSize = false, onPageSizeChange, pageSizeOptions = [10, 20, 50, 100],
}) => {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const currentPage = Math.min(Math.max(1, page), totalPages);
  const startItem = total === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, total);

  // Build page button list: always show first, last, current, and neighbors
  const pages: (number | '…')[] = [];
  const add = (p: number | '…') => { if (!pages.includes(p)) pages.push(p); };
  add(1);
  if (currentPage - 2 > 2) add('…');
  for (let p = Math.max(2, currentPage - 1); p <= Math.min(totalPages - 1, currentPage + 1); p++) add(p);
  if (currentPage + 2 < totalPages - 1) add('…');
  if (totalPages > 1) add(totalPages);

  if (total === 0) return null;

  return (
    <div className={`flex flex-col sm:flex-row items-center justify-between gap-3 px-4 py-3 ${className}`}>
      <div className="flex items-center gap-3 text-2xs text-ink-500 dark:text-ink-400 nums">
        <span>
          Showing <strong className="text-ink-700 dark:text-ink-200">{startItem}</strong>–<strong className="text-ink-700 dark:text-ink-200">{endItem}</strong> of <strong className="text-ink-700 dark:text-ink-200">{total}</strong>
        </span>
        {showPageSize && onPageSizeChange && (
          <select
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
            className="bg-ink-0 dark:bg-ink-850 border border-ink-200 dark:border-ink-700 rounded-md px-2 py-1 text-2xs text-ink-700 dark:text-ink-200 focus:outline-none focus:ring-2 focus:ring-ink-900/10 dark:focus:ring-ink-100/10"
            aria-label="Rows per page"
          >
            {pageSizeOptions.map(opt => <option key={opt} value={opt}>{opt} / page</option>)}
          </select>
        )}
      </div>
      <div className="flex items-center gap-1">
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage <= 1}
          className="btn-icon-sm disabled:opacity-30 disabled:cursor-not-allowed"
          aria-label="Previous page"
        >
          <svg viewBox="0 0 24 24" className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M15 18l-6-6 6-6" />
          </svg>
        </button>
        {pages.map((p, idx) =>
          p === '…' ? (
            <span key={`ellipsis-${idx}`} className="px-2 text-2xs text-ink-400">…</span>
          ) : (
            <button
              key={p}
              onClick={() => onPageChange(p)}
              className={`min-w-8 h-8 px-2 rounded-md text-xs font-medium nums transition-colors ${
                p === currentPage
                  ? 'bg-ink-900 dark:bg-ink-50 text-ink-50 dark:text-ink-900'
                  : 'text-ink-600 dark:text-ink-300 hover:bg-ink-100 dark:hover:bg-ink-800'
              }`}
              aria-current={p === currentPage ? 'page' : undefined}
            >
              {p}
            </button>
          )
        )}
        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage >= totalPages}
          className="btn-icon-sm disabled:opacity-30 disabled:cursor-not-allowed"
          aria-label="Next page"
        >
          <svg viewBox="0 0 24 24" className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M9 18l6-6-6-6" />
          </svg>
        </button>
      </div>
    </div>
  );
};

/* ─── usePagination hook ─────────────────────────────────────── */
export function usePagination<T>(items: T[], initialPageSize = 10) {
  const [page, setPage] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(initialPageSize);
  const total = items.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const currentPage = Math.min(Math.max(1, page), totalPages);
  const start = (currentPage - 1) * pageSize;
  const end = start + pageSize;
  const paginatedItems = items.slice(start, end);

  // Reset to page 1 if items shrink below current page
  React.useEffect(() => {
    if (page > totalPages) setPage(1);
  }, [page, totalPages]);

  return {
    page: currentPage,
    pageSize,
    total,
    totalPages,
    paginatedItems,
    setPage,
    setPageSize: (size: number) => { setPageSize(size); setPage(1); },
  };
}

export default {
  BrandMark, CocoaPod, Card, SectionHeader, MetricTile, StatusPill, SmartStatus,
  Spinner, EmptyState, ProgressBar, Skeleton, IconButton, Divider, TopBar,
  BottomNav, SegmentedTabs, Sheet, Avatar, Alert, Pagination,
};
